import { API_PORTFOLIO, API_PORTFOLIO_DETAIL, API_PORTFOLIO_UPDATE, API_PORTFOLIO_EXECUTE } from 'api/endpoints';
import { observable, action } from 'mobx';
import { Currency } from 'enums';
import { f2p } from 'helpers/helpers';
import { FormikHelpers } from 'formik';
import { FormStore } from 'stores/formStore';
import { Section } from 'pages/Admin/Portfolios/PortfolioModal';
import { PortfolioType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { SingleValueResponse } from 'common/interfaces';

export enum CustomType {
  'CUSTOM' = 'CUSTOM',
  'INTERMEDIARY' = 'INTERMEDIARY',
}

export const savedPortfoliosKey = 'admin-saved-portfolios';

export interface Portfolio {
  id?: string;
  title: string;
  description: string;
  key: string;
  currency: Currency;
  code?: { profile: number; code: string };
  hedgingEur: number;
  hedgingUsd: number;
  lastUpdate?: string;
  validFrom?: string;
  readyForUpdate?: boolean;
  used?: boolean;
  customType?: CustomType;
  intermediaryCode?: string;
  type?: PortfolioType;
  riskProfile?: number;
  ordinalCode?: number;
}

export const initialValues = {
  title: '',
  description: '',
  key: '',
  currency: Currency.NONE,
  hedgingEur: 100,
  hedgingUsd: 100,
};

export class AdminPortfoliosStore extends FormStore<Portfolio> {
  @observable list: Portfolio[] = [];
  @observable data: Portfolio | null = null;
  @observable ready = false;
  @observable savedPortfolios: { ids: string[] } = {ids: []};

  @action
  resetData = () => this.data = null;

  @action
  fetchList = async (withLoading: boolean = true) => {
    const { uiStore, apiStore } = this.rootStore.stores;
    if (withLoading) uiStore.setLoading(true);
    const response = await apiStore.get<Portfolio[]>(API_PORTFOLIO);
    if (response?.data) {
      this.list = response.data;
      this.ready = this.list.some((p) => p.readyForUpdate);
      this.savedPortfolios = JSON.parse(localStorage.getItem(savedPortfoliosKey) || '{ "ids": [] }');
    }
    if (withLoading) uiStore.setLoading(false);
  };

  @action
  deletePortfolio = async (id: string) => {
    const { uiStore, apiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    await apiStore.delete(API_PORTFOLIO_DETAIL(id));
    uiStore.setLoading(false);
  };

  @action
  fetchPortfolio = async (id: string, clone?: boolean) => {
    const { uiStore, apiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = clone
      ? await apiStore.patch<Portfolio>(API_PORTFOLIO_DETAIL(id))
      : await apiStore.get<Portfolio>(API_PORTFOLIO_DETAIL(id));
    if (response?.data) {
      this.data = response.data;
      this.data.hedgingEur = f2p(response.data.hedgingEur);
      this.data.hedgingUsd = f2p(response.data.hedgingUsd);
    }
    uiStore.setLoading(false);
  };

  @action getClonedId = async (id: string) => {
    const { uiStore, apiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = await apiStore.patch<SingleValueResponse<string>>(API_PORTFOLIO_DETAIL(id));
    uiStore.setLoading(false);
    return !!response?.data;
  };

  @action markForUpdate = async (id: string, update: boolean) => {
    const { apiStore } = this.rootStore.stores;
    this.list = this.list.map((p) => ({ ...p, locked: true, marking: p.id === id }));
    await apiStore.post(API_PORTFOLIO_UPDATE(id), { value: update });
    await this.fetchList(false);
  };

  @action handleNext = async (
    data: Portfolio,
    formikHelpers: FormikHelpers<Portfolio>,
    setSection?: Function,
    setId?: Function,
  ) => {
    const response = await this.handleSubmit(data, formikHelpers);
    if (response) {
      if (this.data) {
        this.data.hedgingEur = f2p(this.data.hedgingEur);
        this.data.hedgingUsd = f2p(this.data.hedgingUsd);
      }
      if (setSection && setId) {
        setId(this.data?.id!);
        setSection(Section.ASSETS);
      }
    }
  };

  async handleSubmit(values: Portfolio, formikHelpers: FormikHelpers<Portfolio>): Promise<boolean> {
    const data: Portfolio = JSON.parse(JSON.stringify(values));
    data.hedgingEur = values.hedgingEur / 100;
    data.hedgingUsd = values.hedgingUsd / 100;

    return await this.submitData(API_PORTFOLIO, data, {
      formikHelpers,
      singleValueResponse: false,
      //validationPrefixDepth: 2,
      reset: false,
    });
  }

  @action execute = async () => {
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<{ value: boolean }>>(
      API_PORTFOLIO_EXECUTE,
    );
    const ok = !!response?.data?.value;
    if (ok) localStorage.setItem(savedPortfoliosKey, '{ "ids": [] }');
    return ok
  };
}
